
import { FeatureGate } from '../components/FeatureGate';
import { FeatureNotAvailablePage } from './FeatureNotAvailablePage';

/**
 * Premium Features page - only available when PREMIUM_FEATURES_ENABLED flag is true
 */
export function PremiumFeaturesPage() {
  return (
    <FeatureGate 
      flag="PREMIUM_FEATURES_ENABLED"
      fallback={<FeatureNotAvailablePage featureName="Premium Features" />}
    >
      <div className="min-h-screen bg-gradient-to-br from-treasure/10 via-white to-treasure/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-urban mb-4">
              <span className="mr-2">⭐</span>
              Premium Features
            </h1>
            <p className="text-urban/70 max-w-2xl mx-auto">
              Unlock exclusive features and enhanced capabilities with our premium offerings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Extended Search Radius */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-treasure/20">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-treasure to-treasure-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-white">🌍</span>
                </div>
                <h3 className="text-xl font-semibold text-urban">Extended Search Radius</h3>
              </div>
              <ul className="space-y-2 text-urban/70 mb-6">
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Search up to 100km radius
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Cross-city treasure hunting
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Priority notifications
                </li>
              </ul>
              <button className="w-full bg-treasure hover:bg-treasure-600 text-white font-semibold py-3 rounded-full transition-all duration-200">
                Upgrade Now
              </button>
            </div>

            {/* AI-Powered Insights */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-treasure/20">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-treasure to-treasure-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-white">🤖</span>
                </div>
                <h3 className="text-xl font-semibold text-urban">AI-Powered Insights</h3>
              </div>
              <ul className="space-y-2 text-urban/70 mb-6">
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Automatic item categorization
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Value estimation
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Quality assessment
                </li>
              </ul>
              <button className="w-full bg-treasure hover:bg-treasure-600 text-white font-semibold py-3 rounded-full transition-all duration-200">
                Learn More
              </button>
            </div>

            {/* Premium Analytics */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-treasure/20">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-treasure to-treasure-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-white">📊</span>
                </div>
                <h3 className="text-xl font-semibold text-urban">Premium Analytics</h3>
              </div>
              <ul className="space-y-2 text-urban/70 mb-6">
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Detailed find statistics
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Trend analysis
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Export reports
                </li>
              </ul>
              <button className="w-full bg-treasure hover:bg-treasure-600 text-white font-semibold py-3 rounded-full transition-all duration-200">
                View Demo
              </button>
            </div>

            {/* Early Access */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-treasure/20">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-treasure to-treasure-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-white">🚀</span>
                </div>
                <h3 className="text-xl font-semibold text-urban">Early Access</h3>
              </div>
              <ul className="space-y-2 text-urban/70 mb-6">
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Beta feature access
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Priority support
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Influence development
                </li>
              </ul>
              <button className="w-full bg-treasure hover:bg-treasure-600 text-white font-semibold py-3 rounded-full transition-all duration-200">
                Join Beta
              </button>
            </div>

            {/* Unlimited Submissions */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-treasure/20">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-treasure to-treasure-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-white">📤</span>
                </div>
                <h3 className="text-xl font-semibold text-urban">Unlimited Submissions</h3>
              </div>
              <ul className="space-y-2 text-urban/70 mb-6">
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  No daily limits
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Bulk upload tools
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Advanced editing
                </li>
              </ul>
              <button className="w-full bg-treasure hover:bg-treasure-600 text-white font-semibold py-3 rounded-full transition-all duration-200">
                Upgrade
              </button>
            </div>

            {/* Community Features */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-treasure/20">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-treasure to-treasure-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-white">👥</span>
                </div>
                <h3 className="text-xl font-semibold text-urban">Community Features</h3>
              </div>
              <ul className="space-y-2 text-urban/70 mb-6">
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Private groups
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Direct messaging
                </li>
                <li className="flex items-center">
                  <span className="text-treasure mr-2">✓</span>
                  Exclusive events
                </li>
              </ul>
              <button className="w-full bg-treasure hover:bg-treasure-600 text-white font-semibold py-3 rounded-full transition-all duration-200">
                Join Community
              </button>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="mt-12 text-center">
            <h2 className="text-2xl font-bold text-urban mb-8">Choose Your Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {/* Free Plan */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-urban/10">
                <h3 className="text-lg font-semibold text-urban mb-2">Free</h3>
                <div className="text-3xl font-bold text-urban mb-4">$0<span className="text-sm font-normal">/month</span></div>
                <ul className="space-y-2 text-sm text-urban/70 mb-6">
                  <li>✓ Basic search (5km radius)</li>
                  <li>✓ 5 submissions per day</li>
                  <li>✓ Community access</li>
                </ul>
                <button className="w-full bg-urban/10 text-urban font-semibold py-2 rounded-full">
                  Current Plan
                </button>
              </div>

              {/* Premium Plan */}
              <div className="bg-gradient-to-br from-treasure/10 to-treasure/5 rounded-2xl shadow-lg p-6 border-2 border-treasure relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-treasure text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-urban mb-2">Premium</h3>
                <div className="text-3xl font-bold text-urban mb-4">$9.99<span className="text-sm font-normal">/month</span></div>
                <ul className="space-y-2 text-sm text-urban/70 mb-6">
                  <li>✓ Extended search (50km radius)</li>
                  <li>✓ Unlimited submissions</li>
                  <li>✓ AI-powered insights</li>
                  <li>✓ Premium analytics</li>
                  <li>✓ Priority support</li>
                </ul>
                <button className="w-full bg-treasure hover:bg-treasure-600 text-white font-semibold py-2 rounded-full transition-all duration-200">
                  Upgrade Now
                </button>
              </div>

              {/* Pro Plan */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-urban/10">
                <h3 className="text-lg font-semibold text-urban mb-2">Pro</h3>
                <div className="text-3xl font-bold text-urban mb-4">$19.99<span className="text-sm font-normal">/month</span></div>
                <ul className="space-y-2 text-sm text-urban/70 mb-6">
                  <li>✓ Everything in Premium</li>
                  <li>✓ Global search (unlimited)</li>
                  <li>✓ Advanced analytics</li>
                  <li>✓ API access</li>
                  <li>✓ White-label options</li>
                </ul>
                <button className="w-full bg-cardboard hover:bg-cardboard-600 text-white font-semibold py-2 rounded-full transition-all duration-200">
                  Contact Sales
                </button>
              </div>
            </div>
          </div>

          {/* Feature Flag Debug Info */}
          {import.meta.env.DEV && (
            <div className="mt-8 bg-white rounded-lg p-4 border border-urban/10">
              <h3 className="text-sm font-semibold text-urban mb-2">Feature Flag Debug</h3>
              <p className="text-xs text-urban/70">
                This page is controlled by the PREMIUM_FEATURES_ENABLED feature flag.
                It will only be visible when the flag is enabled.
              </p>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}